import { categoryForMetric, MetricCategory } from '@chiroup/core/constants/metricCategories';
import React, { useMemo } from 'react';
import MetricCard from './MetricCard';

type Props = {
  category: MetricCategory;
  metrics: Record<string, number>;
  previousMetrics: Record<string, number>;
  dailyByMetric: Record<string, number[]>;
  onMetricClick: (metricKey: string) => void;
  loading?: boolean;
};

const SkeletonCard: React.FC = () => (
  <div className="rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex justify-between">
      <div className="h-7 w-12 bg-gray-200 rounded animate-pulse" />
      <div className="h-3 w-8 bg-gray-100 rounded animate-pulse" />
    </div>
    <div className="h-3 w-24 bg-gray-100 rounded mt-2 animate-pulse" />
    <div className="h-6 mt-3 bg-gray-50 rounded animate-pulse" />
  </div>
);

const MetricGrid: React.FC<Props> = ({
  category,
  metrics,
  previousMetrics,
  dailyByMetric,
  onMetricClick,
  loading,
}) => {
  // All metrics in this category (union of current & previous keys, plus any metric
  // we have a category mapping for that landed here).
  const items = useMemo(() => {
    const keys = new Set<string>();
    for (const k of Object.keys(metrics)) {
      if (categoryForMetric(k) === category) keys.add(k);
    }
    for (const k of Object.keys(previousMetrics)) {
      if (categoryForMetric(k) === category) keys.add(k);
    }

    return Array.from(keys)
      .map(k => ({
        metricKey: k,
        value: metrics[k] ?? 0,
        previousValue: previousMetrics[k] ?? 0,
      }))
      .sort((a, b) => b.value - a.value);
  }, [category, metrics, previousMetrics]);

  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-12 text-center">
        <p className="text-sm text-gray-500">No activity in this category for the selected range</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {items.map(item => (
        <MetricCard
          key={item.metricKey}
          metricKey={item.metricKey}
          value={item.value}
          previousValue={item.previousValue}
          series={dailyByMetric[item.metricKey]}
          onClick={() => onMetricClick(item.metricKey)}
        />
      ))}
    </div>
  );
};

export default MetricGrid;
